import type { Project, ProjectUpdate } from "@repo/app-types/entities/project";
import {
  getProjectAndJobTypeProperty,
  JobTypeProperty,
  ResumeCollectionType,
} from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { type FC, useState } from "react";
import { useUpdateJobTypeMutation } from "../../../../api/entities/jobType.js";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";

// "SimpleSelect" uses the placeholder if you pass a "null" value.
// So we need to pass a string in order to display the correct option in the dropdown.
const noResumeCollectionValue = "no_resume_collection";
const resumeCollectionOptions: SimpleSelectOption<
  typeof noResumeCollectionValue | ResumeCollectionType
>[] = [
  {
    value: noResumeCollectionValue,
    label: "None",
  },
  {
    value: ResumeCollectionType.BeforeInterview,
    label: "Before the interview",
  },
  {
    value: ResumeCollectionType.AfterInterview,
    label: "After the interview",
  },
];

interface RecruitingOtherSettingsProps {
  onSaveProject: (update: ProjectUpdate) => void;
  project: Project;
}

export const RecruitingOtherSettings: FC<RecruitingOtherSettingsProps> = ({
  onSaveProject,
  project,
}) => {
  const [updateJobType] = useUpdateJobTypeMutation();

  const [shouldRequestResume, setShouldRequestResume] = useState(
    getProjectAndJobTypeProperty(project, JobTypeProperty.ResumeCollectionType),
  );

  const [shouldForceFullscreen, setShouldForceFullscreen] = useState(
    getProjectAndJobTypeProperty(
      project,
      JobTypeProperty.ShouldForceFullscreen,
    ),
  );
  const [shouldRecordVideo, setShouldRecordVideo] = useState(
    getProjectAndJobTypeProperty(project, JobTypeProperty.ShouldRecordVideo),
  );

  const onChangeShouldRequestResume = (
    option: ResumeCollectionType | typeof noResumeCollectionValue,
  ) => {
    const newResumeCollectionType =
      option === noResumeCollectionValue ? null : option;

    setShouldRequestResume(newResumeCollectionType);

    onSaveProject({
      id: project.id,
      settings: {
        ...project.settings,
        resume_collection_type: newResumeCollectionType,
      },
    });

    if (project.job_type) {
      void updateJobType({
        id: project.job_type.id,
        settings: {
          ...project.job_type.settings,
          resume_collection_type: newResumeCollectionType,
        },
      });
    }
  };

  const onChangeShouldForceFullscreen = (isChecked: boolean) => {
    setShouldForceFullscreen(isChecked);
    onSaveProject({
      id: project.id,
      settings: {
        ...project.settings,
        should_force_fullscreen: isChecked,
      },
    });

    if (project.job_type) {
      void updateJobType({
        id: project.job_type.id,
        settings: {
          ...project.job_type.settings,
          should_force_fullscreen: isChecked,
        },
      });
    }
  };

  const onChangeShouldRecordVideo = (isChecked: boolean) => {
    setShouldRecordVideo(isChecked);
    onSaveProject({
      id: project.id,
      settings: {
        ...project.settings,
        should_record_video: isChecked,
      },
    });

    if (project.job_type) {
      void updateJobType({
        id: project.job_type.id,
        settings: {
          ...project.job_type.settings,
          should_record_video: isChecked,
        },
      });
    }
  };

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <>
          <div className="space-y-3">
            <div className="mt-2 w-[200px]">
              <SimpleSelect
                label="Resume collection"
                options={resumeCollectionOptions}
                value={shouldRequestResume || noResumeCollectionValue}
                onChange={onChangeShouldRequestResume}
                isDisabled={!allowed}
              />
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              Whether candidates should upload their resume when taking their
              interview, if it hasn't already been provided. If the resume is
              collected before the interview, interview questions will
              automatically be personalized to the candidate's background.
            </div>

            <div>
              <Label size={Size.SMALL} className="mb-2">
                Fullscreen mode
              </Label>
              <Checkbox
                isChecked={shouldForceFullscreen}
                onChange={onChangeShouldForceFullscreen}
                label="Force the interview to be conducted in a full-screen browser window to help prevent possible cheating."
                isDisabled={!allowed}
              />
              <div className="mt-2 text-sm text-muted-foreground">
                Candidates that exit fullscreen mode will be flagged.
              </div>
            </div>

            <div>
              <Label size={Size.SMALL} className="mb-2">
                Record video
              </Label>
              <Checkbox
                isChecked={shouldRecordVideo}
                onChange={onChangeShouldRecordVideo}
                label="Record video during the interview."
                isDisabled={!allowed}
              />
              <div className="mt-2 text-sm text-muted-foreground">
                Candidates will be required to provide webcam access and conduct
                their interview on a desktop computer.
              </div>
            </div>
          </div>
        </>
      )}
    </Can>
  );
};
